import {
	adminAddTerminalToProfileApi,
	adminRemoveTerminalFromProfileApi,
} from "api/administration";
import { getTerminalsApi, getUserSettingTerminalsFilterApi } from "api/common";

import { t } from "i18next";
import { CustomError } from "interface";
import { UserProfile } from "interface/user";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useInfoStore } from "store/error";
import CustomButton from "ui/Button";
import CustomDropDownList from "ui/dropDown";
import { DropdownRenderItem } from "ui/dropdownItemList";
import { COLOR, ICON_NAMES } from "utils/constant";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { useUserStore } from "store/user";

type TerminalItem = Record<string, any>;

const getTerminalValue = (terminal: TerminalItem, keys: string[]) => {
	for (const key of keys) {
		const value = terminal[key];
		if (value !== undefined && value !== null && `${value}`.trim() !== "") {
			return `${value}`;
		}
	}

	return "";
};

const getTerminalId = (terminal: TerminalItem) => {
	return getTerminalValue(terminal, ["id", "code", "Id", "Code"]);
};

export const UserTerminalTab: FC<{
	user: UserProfile;
	setUser: (user: UserProfile) => void;
	setUserUpdated: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ user, setUser, setUserUpdated }) => {
	let { id } = useParams();
	const { setError } = useInfoStore();
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	const [selectedTerminal, setSelectedTerminal] = useState<{
		code: string;
		name: string;
		displayName: string;
	} | null>(null);
	const { isAdmin, isCompanyAdmin } = useUserStore();

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const addTerminalToUser = () => {
		adminAddTerminalToProfileApi({
			userId: id as string,
			terminalId: selectedTerminal?.code as string,
		})
			.then(() => {
				setUserUpdated((prev) => !prev);
				setSelectedTerminal(null);
			})
			.catch((error) => {
				const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
				const showCustomError = true;
				setError(err, showCustomError);
			})
			.finally(() => {});
	};

	const removeTerminalFromUser = (terminal: any) => {
		if (!terminal) return;
		const terminalId = getTerminalId(terminal as TerminalItem);
		if (!terminalId) return;

		adminRemoveTerminalFromProfileApi({
			userId: id as string,
			terminalId,
		})
			.then(() => {
				setUserUpdated((prev) => !prev);
			})
			.catch((error) => {
				const err = new CustomError(t("ErrorTitle", "ErrorDescription"));
				const showCustomError = true;
				setError(err, showCustomError);
			})
			.finally(() => {});
	};

	return (
		<div
			className="user-information-form user-information-split-section"
			style={{
				display: "flex",
				flexDirection: isMobileView ? "column" : "row",
				justifyContent: "space-between",
				gap: "30px",
			}}
		>
			<div className="user-information-form-block" style={{ width: isMobileView ? "100%" : "50%" }}>
				<div style={{ marginTop: "20px" }}>
						<div style={{}}>
							<CustomDropDownList
								onSelect={(terminal) => {
									setSelectedTerminal(terminal);
								}}
								data={[]}
								value={{
									code: selectedTerminal?.code || null,
									displayName:
										selectedTerminal?.displayName ||
										`${t("Choose")}  ${t("Terminal")}`,
								}}
								itemRender={DropdownRenderItem}
								dataItemKey="code"
								textField="displayName"
								label={t("Terminal")}
								//defaultItem={isAdmin ? { id: null, displayName: t("All") } : undefined}
								filterable={isAdmin ? true : false}
								serverFilter={isAdmin ? true : false}
								serverFilterApi={isAdmin ? getTerminalsApi : undefined}
								serverApi={isAdmin ? undefined : getUserSettingTerminalsFilterApi}
							/>
						</div>
				</div>
				<div
					className="user-information-button-row"
					style={{ marginTop: "20px", display: "flex", gap: "10px" }}
				>
					<CustomButton
						label={t("Add") + " " + t("Terminal")}
						onClick={() => addTerminalToUser()}
						disabled={!selectedTerminal}
						bgColor={COLOR.Green}
						color="white"
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
			<div
				className="user-information-form-block"
				style={{
					width: isMobileView ? "100%" : "50%",
					display: "flex",
					justifyContent: "flex-start",
					flexDirection: "column",
					gap: "10px",
				}}
			>
				<div style={{ marginBottom: "-30px", paddingTop: "10px" }}>
					{t("Terminal")}
				</div>
				<div className="user-information-grid-shell" style={{}}>
					{isMobileView ? (
						<UserTerminalMobileList
							terminals={(user.terminals || []) as TerminalItem[]}
							removeTerminalFromUser={removeTerminalFromUser}
						/>
					) : (
						<GridWithGroupPaging
							gridData={user.terminals || []}
							className="hide-virtual-scroll"
							pageable={false}
							itemAboveHeight={200}
							gridColumns={[
								{
									field: "id",
									title: t("Code"),
									width: "120px",
								},
								{
									field: "name",
									title: t("Name"),
								},
								{
									field: "place",
									title: t("Address"),
								},
								{
									width: "100px",
									cells: {
										data: (props: any) => {
											const { dataItem } = props;
											return (
												<td>
													<CustomButton
														label=""
														iconName={ICON_NAMES.Delete}
														btnStyle={{
															backgroundColor: COLOR.Grey98,
															height: "26px",
														}}
														onClick={() => {
															removeTerminalFromUser(dataItem);
														}}
													/>
												</td>
											);
										},
									},
								},
							]}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

const UserTerminalMobileList: FC<{
	terminals: TerminalItem[];
	removeTerminalFromUser: (terminal: TerminalItem) => void;
}> = ({ terminals, removeTerminalFromUser }) => {
	if (terminals.length === 0) {
		return <div className="user-information-mobile-empty">{t("NoRows")}</div>;
	}

	return (
		<div className="user-information-mobile-list">
			{terminals.map((terminal, index) => {
				const code = getTerminalValue(terminal, ["id", "code", "Id", "Code"]) || "-";
				const name = getTerminalValue(terminal, ["name", "Name"]) || "-";
				const place = getTerminalValue(terminal, ["place", "Place"]) || "-";
				const key = `${code}-${index}`;

				return (
					<div key={key} className="user-information-mobile-card">
						<div className="user-information-mobile-card-header">
							<div className="user-information-mobile-card-badge">{code}</div>
							<div className="user-information-mobile-card-title">{name}</div>
							<div className="user-information-mobile-card-subtitle">{place}</div>
						</div>
						<div className="user-information-mobile-card-body">
							<div>
								<span>{t("Code")}</span>
								<strong>{code}</strong>
							</div>
							<div>
								<span>{t("Name")}</span>
								<strong>{name}</strong>
							</div>
							<div>
								<span>{t("Address")}</span>
								<strong>{place}</strong>
							</div>
						</div>
						<div className="user-information-mobile-card-actions">
							<button
								type="button"
								className="user-information-mobile-icon-btn"
								onClick={() => removeTerminalFromUser(terminal)}
								title={t("Delete")}
							>
								<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("Delete")} />
							</button>
						</div>
					</div>
				);
			})}
		</div>
	);
};
